import {
    Box,
    Button,
    CardContent,
    CardHeader,
    Container,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StyledCard } from "../StepMainEdit";
import {
    VendorCardTitle,
    VendorListHolder,
} from "./vendorReg";
import BankDetailsContent1 from "../../../bankDetails/components/bankDetailsContent1";
import { getBanks } from "../../../bankDetails/redux/bankDetailsSlice";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { LeftDetailsBankCard, RightDetailsBankCard } from "../../../bankDetails/components/bankAccountsContent";
import LoadingButton from "@mui/lab/LoadingButton";
import { message } from "antd";
import { submitVendorEdit } from "../../redux/vendorRegistrationSlice";
import LocalStorage, { SESSION_KEY } from "../../../../utilities/localStorage";
import { useFeature } from "../../../../common/featureFlags/FeatureProvider";

const BankCard = ({ item, is_domestic, onEdit, isLocked }) => {
    const bankData = item?.item || item;
    const {
        id,
        bank_name,
        city,
        branch_name,
        account_name,
        account_number,
        is_primary_account,
        currency,
    } = bankData;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <StyledCard sx={{ border: "1px solid #EAEAEF", mb: 2 }}>
            <CardHeader
                action={
                    !isLocked && (
                        <IconButton aria-label="settings" onClick={handleClick}>
                            <MoreVertIcon />
                        </IconButton>
                    )
                }
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                            {bank_name}
                        </Typography>
                        {is_primary_account ? (
                            <Box
                                sx={{
                                    fontSize: "12px",
                                    borderRadius: "15px",
                                    background: "#D55EE1",
                                    padding: "2px 12px",
                                    color: "white",
                                    ml: 1,
                                }}
                            >
                                Primary
                            </Box>
                        ) : null}
                    </Box>
                }
            />
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {!isLocked && <MenuItem onClick={() => { handleClose(); onEdit(id); }}>Edit</MenuItem>}
            </Menu>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid item xs={6}><LeftDetailsBankCard>City:</LeftDetailsBankCard></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><RightDetailsBankCard>{city}</RightDetailsBankCard></Grid>
                    <Grid item xs={6}><LeftDetailsBankCard>Branch:</LeftDetailsBankCard></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><RightDetailsBankCard>{branch_name}</RightDetailsBankCard></Grid>
                    <Grid item xs={6}><LeftDetailsBankCard>A/C name:</LeftDetailsBankCard></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><RightDetailsBankCard>{account_name}</RightDetailsBankCard></Grid>
                    <Grid item xs={6}><LeftDetailsBankCard>{is_domestic ? "A/C number:" : "IBAN Number"}</LeftDetailsBankCard></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><RightDetailsBankCard>{account_number}</RightDetailsBankCard></Grid>
                </Grid>
            </CardContent>
        </StyledCard>
    );
};

export default function BankDetailsManager({
    vendor_id,
    user_id,
    handleBack,
    activeStep,
    is_domestic,
    company_id,
    setSkipped,
    setActiveStep,
    isStepSkipped,
    skipped,
    isLocked,
    isCreation = false,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loginUserDetails = LocalStorage.getItem(SESSION_KEY);
    const { banks, loading } = useSelector((state) => state.bankdetails);
    const { createdUser } = useSelector((state) => state.vendorRegistration);
    const [view, setView] = useState("list"); // list, add, edit
    const [editingBankId, setEditingBankId] = useState(null);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [isFinishing, setIsFinishing] = useState(false);
    const vendor_approval_workflow = useFeature("vendor_approval_workflow");
    const isVendorEditWorkflow = useFeature("vendor_edit_workflow");
    const isAutoApproveVendor = useFeature("auto_approve_vendor");
    const showRequestedDocs = useFeature("requested_documents");
    const disableMultipleBanks = useFeature("disable_multiple_banks");

    const hasWorkflow = isVendorEditWorkflow || isAutoApproveVendor || vendor_approval_workflow;
    const shouldFinish = vendor_approval_workflow || isAutoApproveVendor;
    const isOfficer = loginUserDetails.roles.includes("officer");

    const currentVendorId = vendor_id || createdUser?.vendor_id || loginUserDetails?.vendor_id || loginUserDetails?.id;

    const handleFinish = () => {

        if (!currentVendorId) {
            message.error("Vendor ID is missing. Please refresh the page and try again.");
            return;
        }

        if (isCreation && !hasWorkflow) {
            message.success("Registration completed successfully!");
            navigate("/app");
            return;
        }

        const payload = {
            vendor_id: currentVendorId,
            is_finish: shouldFinish ? true : false,
        };

        setIsFinishing(true);
        dispatch(submitVendorEdit(payload))
            .unwrap()
            .then((res) => {
                setIsFinishing(false);
                if (res?.success) {
                    message.success(res?.message || "Edits submitted successfully.");
                } else {
                    message.info(res?.message || "No pending edits to submit.");
                }
                navigate("/app");
            })
            .catch((err) => {
                setIsFinishing(false);
                message.error("Failed to submit edits.");
            });
    };

    useEffect(() => {
        if (currentVendorId || user_id) {
            dispatch(getBanks(currentVendorId || user_id));
        }
    }, [dispatch, currentVendorId, user_id]);

    const handleAdd = () => {
        setView("add");
    };

    const handleEdit = (id) => {
        setEditingBankId(id);
        setView("edit");
    };

    const handleCancel = () => {
        setView("list");
        setEditingBankId(null);
    };

    const handleSuccess = () => {
        if (disableMultipleBanks) {
            let newSkipped = skipped;
            if (isStepSkipped && isStepSkipped(activeStep)) {
                newSkipped = new Set(newSkipped.values());
                newSkipped.delete(activeStep);
            }
            if (setActiveStep) {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
            if (setSkipped) {
                setSkipped(newSkipped);
            }
        } else {
            setView("list");
            setEditingBankId(null);
            dispatch(getBanks(currentVendorId || user_id));
            setHasPendingChanges(true);
        }
    };

    if (view === "add" || view === "edit") {
        return (
            <BankDetailsContent1
                vendor_id={currentVendorId}
                createdUser={{ id: user_id }}
                is_domestic={is_domestic}
                company_id={company_id}
                bankInfoId={editingBankId}
                isVendorEditStepper={true}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
                isLocked={isLocked}
            />
        );
    }

    return (
        <Box>
            <StyledCard>
                <CardContent>
                    <VendorCardTitle>{isOfficer ? "Step 4" : "Step 3"}: Bank Details</VendorCardTitle>
                    <VendorListHolder>
                        <li>Manage your bank account here. {vendor_approval_workflow ? "Only one bank account is allowed." : ""}</li>
                        <li>Ensure the account is marked as Primary.</li>
                    </VendorListHolder>
                </CardContent>
            </StyledCard>

            <Grid container spacing={2}>
                {banks && banks.length > 0 ? (
                    banks.map((bank) => (
                        <Grid item xs={12} md={6} key={bank.id}>
                            <BankCard item={bank} is_domestic={is_domestic} onEdit={handleEdit} isLocked={isLocked} />
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography sx={{ textAlign: 'center', my: 4, color: '#666687' }}>
                            No bank accounts added yet.
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {(!banks || banks.length === 0) && !isLocked && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleAdd}
                        sx={{ textTransform: 'none', borderStyle: 'dashed' }}
                    >
                        + Add Bank Account
                    </Button>
                </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "row", pt: 2, justifyContent: "space-between" }}>
                <Button color="inherit" onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                <Tooltip
                    title={(!banks || banks.length === 0) ? "Please add at least one bank account to continue" : ""}
                    arrow
                    placement="top"
                >
                    <span>
                        <LoadingButton
                            sx={{ textTransform: "none", background: "var(--button-color)", width: "200px" }}
                            color="success"
                            variant="contained"
                            onClick={showRequestedDocs ? () => {
                                let newSkipped = skipped;
                                if (isStepSkipped(activeStep)) {
                                    newSkipped = new Set(newSkipped.values());
                                    newSkipped.delete(activeStep);
                                }
                                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                                if (setSkipped) setSkipped(newSkipped);
                            } : handleFinish}
                            disabled={isLocked || (!banks || banks.length === 0) || isFinishing}
                            loading={isFinishing}
                            loadingPosition="start"
                        >
                            {showRequestedDocs ? "Next" : (isLocked ? "Submitted" : "Finish")}
                        </LoadingButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}
